import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { CLIENTS, CLIENT_LOCATIONS } from "@/data/site";

const Portfolio = () => {
  useEffect(() => {
    document.title = "Our Work — Click World Online";
  }, []);
  return (
    <>
      <PageHeader
        eyebrow="Our Work"
        title={<>Brands we've helped <span className="text-electric">click.</span></>}
        text="A selection from the 800+ businesses that have trusted Click World Online since 2017 — and many more behind them."
      />
      <section data-testid="portfolio-list-section" className="bg-navy-deep">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="border-t border-white/5">
            {CLIENTS.map((c, i) => (
              <Reveal key={c.slug} delay={0.03 * i}>
                <Link
                  to={`/portfolio/${c.slug}`}
                  data-testid={`portfolio-row-${c.slug}`}
                  className="group grid grid-cols-[auto_1fr_auto] items-center gap-5 lg:gap-12 py-8 lg:py-12 border-b border-white/5 px-2 lg:px-8 -mx-2 lg:-mx-8 hover:bg-white/[0.03] transition-colors duration-300"
                >
                  <span className="font-display text-sm lg:text-base font-bold text-mist/30 group-hover:text-electric transition-colors duration-300 w-8">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>
                    <span className="block font-display text-3xl lg:text-6xl font-extrabold tracking-tight text-mist group-hover:text-electric transition-colors duration-300">
                      {c.name}
                    </span>
                    <span className="block mt-2 text-sm lg:text-base text-slatec">{c.category}</span>
                    {c.location && <span className="block mt-1 text-xs text-mist/40">{c.location}</span>}
                  </span>
                  <span className="w-12 h-12 lg:w-16 lg:h-16 rounded-full border border-white/10 flex items-center justify-center text-mist/40 group-hover:border-electric group-hover:text-electric group-hover:rotate-45 transition-[transform,border-color,color] duration-500">
                    <ArrowUpRight size={22} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <p className="text-slatec text-sm md:text-base max-w-xl leading-relaxed">
              Plus many more organisations across retail, education, healthcare, hospitality, manufacturing and the
              political space — we've delivered digital work for several political parties as well — including{" "}
              <Link to="/360-experiences" data-testid="portfolio-360-link" className="text-electric hover:underline">
                hundreds of published 360° virtual tours
              </Link>
              .
            </p>
          </Reveal>
          <Reveal className="mt-20">
            <div data-testid="client-locations" className="relative bg-navy-light border border-white/10 rounded-3xl p-8 lg:p-12 grain overflow-hidden">
              <div className="absolute inset-0 dot-grid opacity-15" aria-hidden="true" />
              <div className="relative">
                <h2 className="font-display text-2xl lg:text-3xl font-extrabold tracking-tight text-mist">Where our clients are</h2>
                <p className="mt-3 text-slatec text-sm leading-relaxed max-w-2xl">
                  Places where our clients are located or where we've worked with clients. Most of this work is
                  delivered remotely or through scheduled visits — we don't claim offices in these cities.
                </p>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-4">India</h3>
                    <div className="flex flex-wrap gap-2">
                      {CLIENT_LOCATIONS.india.map((c) => (
                        <span key={c} data-testid={`client-city-${c.split(",")[0].toLowerCase().replace(/\s/g, "-")}`} className="text-sm text-mist/80 border border-white/10 bg-white/5 rounded-full px-4 py-2">
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-4">International</h3>
                    <div className="flex flex-wrap gap-2">
                      {CLIENT_LOCATIONS.international.map((c) => (
                        <span key={c} data-testid={`client-intl-${c.split(",")[0].toLowerCase().replace(/\s/g, "-")}`} className="text-sm text-mist/80 border border-white/10 bg-white/5 rounded-full px-4 py-2">
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <CTASection title="Your brand could be next." text="Tell us about your business — and let's plan the work worth showing off." />
    </>
  );
};

export default Portfolio;
